import { range } from 'lodash';
import moment from 'moment';
import { DateValidationStates } from './DatePicker';

export const dateIsWithinRange = (date: Date, min: Date, max: Date) => {
  if (date.getTime() > max.getTime()) return false;
  if (date.getTime() < min.getTime()) return false;
  return true;
};

export const validateDateInput = (date: string) => {
  const formats = ['MM/DD/YYYY', 'M/D/YYYY', 'MM/D/YYYY', 'M/DD/YYYY'];

  const validInterpretations = formats.map((format) => {
    return moment(date, format, true).isValid() ? moment(date, format, true) : undefined;
  });

  return validInterpretations.filter((date) => date);
};

export const validateTimeInput = (input: string) => {
  const formats = ['H:m:s', 'H:m:ss', 'H:mm:s', 'H:mm:ss', 'HH:m:s', 'HH:m:ss', 'HH:mm:s', 'HH:mm:ss'];

  const validInterpretations = formats.map((format) => {
    return moment(input, format, true).isValid() ? moment(input, format, true) : undefined;
  });

  return validInterpretations.filter((date) => date);
};

export const updateTimeAndKeepDate = (oldDate: Date, newDate: Date) => {
  const dayAsMoment = moment(newDate);
  const currentDateAsMoment = moment(oldDate);

  const day = currentDateAsMoment.date();
  const month = currentDateAsMoment.month(); // Months are 0-indexed in Moment.js
  const year = currentDateAsMoment.year();

  const hours = dayAsMoment.hours();
  const minutes = dayAsMoment.minutes();
  const seconds = dayAsMoment.seconds();

  return moment()
    .year(year)
    .month(month)
    .date(day)
    .hours(hours)
    .minutes(minutes)
    .seconds(seconds)
    .milliseconds(0)
    .toDate();
};

export const updateDateAndKeepTime = (oldDate: Date, newDate: Date) => {
  const dayAsMoment = moment(newDate);
  const currentDateAsMoment = moment(oldDate);

  const day = dayAsMoment.date();
  const month = dayAsMoment.month(); // Months are 0-indexed in Moment.js
  const year = dayAsMoment.year();

  const hours = currentDateAsMoment.hours();
  const minutes = currentDateAsMoment.minutes();
  const seconds = currentDateAsMoment.seconds();

  return moment()
    .year(year)
    .month(month)
    .date(day)
    .hours(hours)
    .minutes(minutes)
    .seconds(seconds)
    .milliseconds(0)
    .toDate();
};

export const validMonthsForYearWithinBounds = (currentYear: number, minDate: Date, maxDate: Date) => {
  return [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ].filter((month) => {
    const monthStart = moment(`${month} ${currentYear}`, 'MMMM YYYY').startOf('month').toDate().getTime();
    const monthEnd = moment(`${month} ${currentYear}`, 'MMMM YYYY').endOf('month').toDate().getTime();

    if (monthStart > maxDate.getTime()) return false;
    if (monthEnd < minDate.getTime()) return false;

    return true;
  });
};

export const validYearsWithinBounds = (minDate: Date, maxDate: Date) => {
  return range(1990, new Date(2038, 1, 1).getFullYear() + 1)
    .filter((year) => {
      if (!minDate) return true;
      if (minDate.getFullYear() > year) {
        return false;
      }
      return true;
    })
    .filter((year) => {
      if (!maxDate) return true;
      if (maxDate.getFullYear() < year) {
        return false;
      }
      return true;
    });
};

const keyIsNumber = (key: string) => {
  return /^[0-9]{0,2}$/.test(key);
};

export const handleMonthKeyDown = (
  currentMonth: string | undefined,
  key: string,
  setFocus: (_: 'month' | 'day' | 'year') => void
) => {
  let newValue;

  if (key === 'Tab') {
    setFocus('day');
  }

  if (!currentMonth) {
    if (keyIsNumber(key)) {
      newValue = key;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      newValue = new Date().getMonth() + 1;
    }
  } else {
    newValue = currentMonth;

    if (keyIsNumber(key)) {
      const attemptNewValue = +(currentMonth + key);
      if (attemptNewValue < 13) {
        newValue = attemptNewValue;
      }
    }

    if (key === 'ArrowUp' && +currentMonth < 12) {
      newValue = +currentMonth + 1;
    }

    if (key === 'ArrowDown' && +currentMonth > 1) {
      newValue = +currentMonth - 1;
    }

    if (key === 'Backspace') {
      newValue = currentMonth.toString().slice(0, -1);
    }
  }

  return newValue;
};

export const handleDayKeyDown = (
  currentDay: string | undefined,
  key: string,
  setFocus: (_: 'month' | 'day' | 'year') => void,
  shift?: boolean
) => {
  let newValue;

  if (key === 'Tab') {
    if (shift) {
      setFocus('month');
      return currentDay;
    }

    setFocus('year');
    return currentDay;
  }

  if (!currentDay) {
    if (keyIsNumber(key)) {
      newValue = key;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      newValue = new Date().getDate();
    }
  } else {
    newValue = currentDay;

    if (keyIsNumber(key)) {
      const attemptNewValue = +(currentDay + key);
      if (attemptNewValue < 32) {
        newValue = attemptNewValue;
      }
    }

    if (key === 'ArrowUp' && +currentDay < 32) {
      newValue = +currentDay + 1;
    }

    if (key === 'ArrowDown' && +currentDay > 1) {
      newValue = +currentDay - 1;
    }

    if (key === 'Backspace') {
      newValue = currentDay.toString().slice(0, -1);
    }
  }

  return newValue;
};

export const handleYearKeyDown = (
  currentYear: string | undefined,
  key: string,
  setFocus: (_: 'month' | 'day' | 'year' | 'hour' | 'minute') => void,
  shift?: boolean,
  allowTimeSelection?: boolean
) => {
  let newValue;

  if (key === 'Tab') {
    if (shift) {
      setFocus('day');
      return currentYear;
    }

    if (allowTimeSelection) {
      setFocus('hour');
      return currentYear;
    }
    setFocus('year');
    return currentYear;
  }

  if (!currentYear) {
    if (keyIsNumber(key)) {
      newValue = key;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      newValue = new Date().getFullYear();
    }
  } else {
    newValue = currentYear;

    if (keyIsNumber(key)) {
      const attemptNewValue = +(currentYear + key);
      if (attemptNewValue.toString().length < 5) {
        newValue = attemptNewValue;
      }
    }

    if (key === 'ArrowUp') {
      newValue = +currentYear + 1;
    }

    if (key === 'ArrowDown') {
      newValue = +currentYear - 1;
    }

    if (key === 'Backspace') {
      newValue = currentYear.toString().slice(0, -1);
    }
  }

  return newValue;
};

export const handleHourKeyDown = (
  currentHour: string | undefined,
  key: string,
  setFocus: (_: 'month' | 'day' | 'year' | 'hour' | 'minute') => void,
  shift?: boolean
) => {
  let newValue;

  if (key === 'Tab') {
    if (shift) {
      setFocus('year');
      return currentHour;
    }

    setFocus('minute');
    return currentHour;
  }

  if (!currentHour) {
    if (keyIsNumber(key)) {
      newValue = key;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      newValue = new Date().getHours();
    }
  } else {
    newValue = currentHour;

    if (keyIsNumber(key)) {
      const attemptNewValue = +(currentHour + key);
      if (attemptNewValue < 23) {
        newValue = attemptNewValue;
      }
    }

    if (key === 'ArrowUp' && +currentHour < 23) {
      newValue = +currentHour + 1;
    }

    if (key === 'ArrowDown' && +currentHour > 1) {
      newValue = +currentHour - 1;
    }

    if (key === 'Backspace') {
      newValue = currentHour.toString().slice(0, -1);
    }
  }

  return newValue;
};

export const handleMinuteKeyDown = (
  currentHour: string | undefined,
  key: string,
  setFocus: (_: 'month' | 'day' | 'year' | 'hour' | 'minute') => void,
  shift?: boolean
) => {
  let newValue;

  if (key === 'Tab') {
    if (shift) {
      setFocus('hour');
      return currentHour;
    }

    setFocus('minute');
    return currentHour;
  }

  if (!currentHour) {
    if (keyIsNumber(key)) {
      newValue = key;
    }

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      newValue = new Date().getDate();
    }
  } else {
    newValue = currentHour;

    if (keyIsNumber(key)) {
      const attemptNewValue = +(currentHour + key);
      if (attemptNewValue < 59) {
        newValue = attemptNewValue;
      }
    }

    if (key === 'ArrowUp' && +currentHour < 59) {
      newValue = +currentHour + 1;
    }

    if (key === 'ArrowDown' && +currentHour > 1) {
      newValue = +currentHour - 1;
    }

    if (key === 'Backspace') {
      newValue = currentHour.toString().slice(0, -1);
    }
  }

  return newValue;
};

export const getValidationStateForDateWithMinAndMax: (args: {
  date: Date;
  min?: Date;
  max?: Date;
}) => DateValidationStates = ({ date, min, max }) => {
  if (min && date.getTime() < min.getTime()) return 'too early';
  if (max && date.getTime() > max.getTime()) return 'too late';
  return 'valid';
};
